/**
 * Cross-tab synchronization for JWT token refresh.
 *
 * Prevents multiple browser tabs from refreshing tokens simultaneously.
 * Uses localStorage for locking and BroadcastChannel for communication.
 */

import { RefreshMessage, WaitingPromise } from './types';

const LOCK_KEY = 'refresh_lock';
const LOCK_TIMEOUT = 10000; // 10 seconds max lock time
const CHANNEL_NAME = 'auth_channel';
const LAST_REFRESH_KEY = 'last_refresh_time';
const REFRESH_COOLDOWN = 30000; // 30 seconds cooldown after refresh
const LOCK_POLL_INTERVAL_MS = 500;

const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

class RefreshLock {
  private channel: BroadcastChannel | null = null;

  private waitingPromises: WaitingPromise[] = [];

  constructor() {
    this.initChannel();
    window.addEventListener('storage', this.handleStorageChange);
  }

  private initChannel(): void {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<RefreshMessage>) => {
        this.handleMessage(event.data);
      };
    }
  }

  private handleMessage(message: RefreshMessage): void {
    if (message.type === 'TOKENS_REFRESHED') {
      this.waitingPromises.forEach((p) => p.resolve());
    } else if (message.type === 'REFRESH_FAILED') {
      this.waitingPromises.forEach((p) => p.reject(new Error('Refresh failed in another tab')));
    }

    this.waitingPromises = [];
  }

  /**
   * Fallback handler for when BroadcastChannel is unavailable.
   */
  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === LOCK_KEY && event.newValue === null) {
      this.waitingPromises.forEach((p) => p.resolve());
      this.waitingPromises = [];
    }
  };

  /**
   * Parse lock value to extract tab ID and timestamp.
   */
  private static parseLock(value: string | null): { tabId: string; timestamp: number } | null {
    if (!value) return null;

    const parts = value.split(':');
    if (parts.length !== 2) {
      return null;
    }

    return {
      tabId: parts[0],
      timestamp: Number(parts[1]),
    };
  }

  /**
   * Create lock value with tab ID and timestamp.
   */
  private static createLockValue(): string {
    return `${TAB_ID}:${Date.now()}`;
  }

  /**
   * Try to acquire the refresh lock.
   * Uses verify-after-write pattern with TAB_ID comparison to handle race conditions.
   */
  static acquire(): boolean {
    const existingLock = RefreshLock.parseLock(localStorage.getItem(LOCK_KEY));

    // If lock exists and is not expired, check if it's ours (re-entrant)
    if (existingLock && Date.now() - existingLock.timestamp < LOCK_TIMEOUT) {
      return existingLock.tabId === TAB_ID;
    }

    // Write our lock
    const ourLock = RefreshLock.createLockValue();
    localStorage.setItem(LOCK_KEY, ourLock);

    // Verify by TAB_ID, not by full value (handles race where timestamp differs)
    const verifyLock = RefreshLock.parseLock(localStorage.getItem(LOCK_KEY));

    // If someone else got the lock, we lost the race
    if (!verifyLock || verifyLock.tabId !== TAB_ID) {
      return false;
    }

    // Double-check to reduce race window - another tab might have written between our write and first verify
    const doubleCheck = RefreshLock.parseLock(localStorage.getItem(LOCK_KEY));

    return doubleCheck?.tabId === TAB_ID;
  }

  /**
   * Release the lock and notify other tabs.
   */
  release(success: boolean): void {
    const lock = RefreshLock.parseLock(localStorage.getItem(LOCK_KEY));

    if (!lock || lock.tabId === TAB_ID) {
      localStorage.removeItem(LOCK_KEY);
    }

    const message: RefreshMessage = success
      ? { type: 'TOKENS_REFRESHED' }
      : { type: 'REFRESH_FAILED' };
    this.channel?.postMessage(message);
    this.handleMessage(message);
  }

  /**
   * Wait for another tab to complete refresh.
   */
  waitForRefresh(): Promise<void> {
    return new Promise((resolve, reject) => {
      let pollInterval: ReturnType<typeof setInterval>;
      let timeout: ReturnType<typeof setTimeout>;

      const clearTimers = (): void => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };

      const waiter: WaitingPromise = {
        resolve: () => {
          clearTimers();
          resolve();
        },
        reject: (error: Error) => {
          clearTimers();
          reject(error);
        },
      };

      const stopWaiting = (): void => {
        this.waitingPromises = this.waitingPromises.filter((p) => p !== waiter);
        waiter.resolve();
      };

      this.waitingPromises.push(waiter);

      pollInterval = setInterval(() => {
        if (!RefreshLock.isLocked()) {
          stopWaiting();
        }
      }, LOCK_POLL_INTERVAL_MS);

      timeout = setTimeout(stopWaiting, LOCK_TIMEOUT);
    });
  }

  /**
   * Check if lock is currently held by any tab.
   */
  static isLocked(): boolean {
    const lock = RefreshLock.parseLock(localStorage.getItem(LOCK_KEY));
    if (!lock) return false;

    return Date.now() - lock.timestamp < LOCK_TIMEOUT;
  }

  /**
   * Check if any tab has recently completed a refresh.
   * This prevents multiple tabs from refreshing when they get 401s at different times.
   */
  static wasRecentlyRefreshed(): boolean {
    const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
    if (!lastRefresh) return false;

    return Date.now() - Number(lastRefresh) < REFRESH_COOLDOWN;
  }

  /**
   * Mark that a refresh just completed (cross-tab).
   */
  static setLastRefreshTime(): void {
    localStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
  }
}

export { REFRESH_COOLDOWN };

export const refreshLock = new RefreshLock();

export { RefreshLock };

export default refreshLock;
